import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  directoryApi, meetingsApi, type MeetingDetail, type ProjectMeetings,
} from '@/lib/apiClient';
import { Badge, Button } from '@/components';
import { isConflictError, isDefiniteRejection } from '@/lib/httpErrors';
import { randomId } from '@/lib/randomId';
import { fmtDate } from '@/lib/dates';
import { fmtSentAt } from '../lib/sentAt';
import {
  attendee, blankItem, draftFrom, moveItem, newDraft, problems, toInput,
  type AttendeeDraft, type ItemDraft, type MeetingDraft,
} from './meetingForm';
import { MeetingStyles } from './meetingStyles';
import { STATUS_TONE } from './meetingForm';

/** How our own people are named on the minutes. */
const OUR_COMPANY = 'East & Westbrook';

interface Props {
  projectId: number;
  /** null: a new meeting. */
  meetingId: number | null;
  lists: ProjectMeetings;
  onClose: () => void;
  /** After a save, with the meeting's id (a new one's is known only then). */
  onSaved: (id: number) => void;
}

/**
 * One meeting (#644): its details, who is invited and who came, and its agenda
 * items with the minutes recorded on each. Saved whole, with Save; a save made
 * from an older copy than the one on the server is refused, saying so, rather
 * than putting back what someone else changed.
 *
 * Someone who can see the job but not edit it gets the same meeting to read.
 */
export default function MeetingEditor({ projectId, meetingId, lists, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const isNew = meetingId == null;
  const key = ['project-meeting', projectId, meetingId];
  const q = useQuery({ queryKey: key, queryFn: () => meetingsApi.get(projectId, meetingId!), enabled: !isNew });
  const canEdit = isNew ? lists.canEdit : (q.data?.canEdit ?? false);
  // The job's team and Contacts, to pick attendees from. Editors only: a reader picks no one.
  const dir = useQuery({ queryKey: ['project-contacts', projectId], queryFn: () => directoryApi.forProject(projectId), enabled: canEdit });

  const [draft, setDraft] = useState<MeetingDraft | null>(() => (isNew ? newDraft() : null));
  const [saved, setSaved] = useState<string>(() => (isNew ? JSON.stringify(toInput(newDraft())) : ''));
  const [version, setVersion] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [tried, setTried] = useState(false);
  // Fixed for this new meeting until it is created, and resent on every retry,
  // so a create whose reply was lost is answered with the meeting it made
  // rather than making a second (Codex on #723; the site photos pattern, #560).
  const requestId = useRef<string>(randomId());

  /** Take the server's copy as the one being edited, and as the baseline for "unsaved changes". */
  const adopt = (m: MeetingDetail) => {
    const d = draftFrom(m);
    setDraft(d);
    setSaved(JSON.stringify(toInput(d)));
    setVersion(m.version);
  };
  const dirty = draft != null && JSON.stringify(toInput(draft)) !== saved;
  // The first copy read, and then any newer one a refetch brings while nothing
  // here is unsaved: reopening a meeting shows the cached copy first, and the
  // fresh one must replace it, or someone else's change stays hidden until a
  // save is refused (Codex on #723). Unsaved edits are never replaced; a save
  // from them gets the 409 and Reload the meeting.
  useEffect(() => {
    if (!q.data) return;
    if (version === null || (!dirty && q.data.version > version)) adopt(q.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, dirty]);
  const leave = () => {
    if (dirty && !window.confirm('Leave this meeting without saving your changes?')) return;
    onClose();
  };
  // A refresh, a closed tab or another site would drop unsaved minutes without
  // asking, so the browser asks first (Codex on #723). Moving elsewhere in the
  // app isn't caught: react-router's useBlocker needs a data router, and the
  // app mounts <BrowserRouter>.
  useEffect(() => {
    if (!dirty) return;
    const ask = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [dirty]);

  const typeLabel = (k: string) => lists.types.find((t) => t.key === k)?.label ?? k;

  if (!isNew && q.isPending) return <p className="mtg-muted"><MeetingStyles />Loading the meeting…</p>;
  if (!isNew && (q.isError || !q.data)) {
    return (
      <div>
        <MeetingStyles />
        <div className="mtg-error" role="alert">Couldn’t load this meeting. {(q.error as Error | null)?.message}</div>
        <p><button type="button" className="mtg-back" onClick={onClose}>← All meetings</button></p>
      </div>
    );
  }
  if (!draft) return null;

  if (!canEdit && q.data) return <MeetingReadView meeting={q.data} lists={lists} onClose={onClose} />;

  const set = (patch: Partial<MeetingDraft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setItem = (k: string, patch: Partial<ItemDraft>) =>
    setDraft((d) => (d ? { ...d, items: d.items.map((i) => (i.key === k ? { ...i, ...patch } : i)) } : d));
  const setAttendee = (k: string, patch: Partial<AttendeeDraft>) =>
    setDraft((d) => (d ? { ...d, attendees: d.attendees.map((a) => (a.key === k ? { ...a, ...patch } : a)) } : d));

  const blockers = problems(draft);

  const save = async () => {
    setTried(true);
    if (blockers.length) return;
    setBusy(true);
    setError(null);
    try {
      const body = toInput(draft);
      const m = isNew
        ? await meetingsApi.create(projectId, { ...body, meetingType: draft.meetingType, clientRequestId: requestId.current })
        : await meetingsApi.update(projectId, meetingId!, { ...body, expectedVersion: version! });
      qc.setQueryData(['project-meeting', projectId, m.id], m);
      void qc.invalidateQueries({ queryKey: ['project-meetings', projectId] });
      void qc.invalidateQueries({ queryKey: ['projects', 'activity', projectId] });
      adopt(m);
      setTried(false);
      if (isNew) onSaved(m.id);
    } catch (e) {
      setError((e as Error).message || 'The meeting wasn’t saved. Try again.');
      // A new meeting has no copy to reload: its 409s (another of the type
      // numbered at the same moment, or this one already made and removed)
      // are answered by saving again (Codex on #723).
      setStale(!isNew && isConflictError(e));
      // A refusal can't succeed on retry with the same request id, so start a
      // fresh one: a definite 4xx, and a 409 too (a lost reply is never a 409;
      // the server answers it with the meeting it made). Anything else
      // (network, 5xx) keeps the id so the retry is recognized.
      if (isNew && (isDefiniteRejection(e) || isConflictError(e))) requestId.current = randomId();
    } finally {
      setBusy(false);
    }
  };

  /** Throw away this copy and take the server's, after a refused save. */
  const reload = async () => {
    const m = await meetingsApi.get(projectId, meetingId!);
    qc.setQueryData(key, m);
    adopt(m);
    setError(null);
    setStale(false);
  };

  const remove = async () => {
    if (!window.confirm('Remove this meeting from the job? Its agenda and minutes go with it.')) return;
    setBusy(true);
    try {
      await meetingsApi.remove(projectId, meetingId!);
      void qc.invalidateQueries({ queryKey: ['project-meetings', projectId] });
      void qc.invalidateQueries({ queryKey: ['projects', 'activity', projectId] });
      onClose();
    } catch (e) {
      setError((e as Error).message || 'The meeting wasn’t removed. Try again.');
      setBusy(false);
    }
  };

  const heading = isNew
    ? `New ${typeLabel(draft.meetingType)} meeting`
    : (q.data!.name);

  return (
    <div className="mtg mtg-form">
      <MeetingStyles />
      <div className="mtg-bar">
        <button type="button" className="mtg-back" onClick={leave}>← All meetings</button>
        <span className="mtg-spacer" />
        {!isNew && <Button variant="danger" size="sm" onClick={() => { void remove(); }} disabled={busy}>Remove</Button>}
        <Button size="sm" onClick={() => { void save(); }} disabled={busy || (!dirty && !isNew)}>
          {busy ? 'Saving…' : isNew ? 'Create meeting' : 'Save'}
        </Button>
      </div>
      {error && (
        <div className="mtg-error" role="alert">
          {error}{' '}
          {stale && <Button variant="secondary" size="sm" onClick={() => { void reload(); }}>Reload the meeting</Button>}
        </div>
      )}
      {tried && blockers.length > 0 && (
        <div className="mtg-error" role="alert">{blockers.join(' ')}</div>
      )}

      <div className="pw-card">
        <h2 className="pw-card-hd">{heading}</h2>
        <div className="mtg-grid">
          {isNew && (
            <label className="mtg-field">
              <span>Type</span>
              <select value={draft.meetingType} onChange={(e) => set({ meetingType: e.target.value })}>
                {lists.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
          )}
          <label className="mtg-field">
            <span>Title <span className="mtg-muted">(optional)</span></span>
            <input value={draft.title} maxLength={200} onChange={(e) => set({ title: e.target.value })}
              placeholder={isNew ? `${typeLabel(draft.meetingType)} Meeting #…` : `${typeLabel(draft.meetingType)} Meeting #${q.data!.meetingNumber}`} />
          </label>
          <label className="mtg-field">
            <span>Starts (Eastern time)</span>
            <input type="datetime-local" value={draft.startsAt} onChange={(e) => set({ startsAt: e.target.value })} />
          </label>
          <label className="mtg-field">
            <span>Location</span>
            <input value={draft.location} maxLength={300} onChange={(e) => set({ location: e.target.value })} placeholder="Job trailer, a call…" />
          </label>
          <label className="mtg-field">
            <span>Status</span>
            <select value={draft.status} onChange={(e) => set({ status: e.target.value })}>
              {lists.statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      <Attendees
        attendees={draft.attendees}
        directory={dir.data}
        onChange={(attendees) => set({ attendees })}
        onEdit={setAttendee}
      />

      <div className="pw-card">
        <div className="mtg-hd">
          <h2 className="pw-card-hd">Agenda and minutes</h2>
          <Button size="sm" variant="secondary" onClick={() => set({ items: [...draft.items, blankItem(draft.items.at(-1)?.section ?? '')] })}>＋ Add item</Button>
        </div>
        {draft.items.length === 0 && <p className="mtg-pad mtg-muted">No items yet. Add the first with Add item.</p>}
        <datalist id={`mtg-responsible-${projectId}`}>
          {[...new Set([OUR_COMPANY, ...draft.attendees.flatMap((a) => [a.name, a.company]).filter(Boolean)])].map((v) => <option key={v} value={v} />)}
        </datalist>
        {draft.items.map((item, i) => (
          <div key={item.key} className="mtg-item" role="group" aria-label={`Item ${i + 1}`}>
            <div className="mtg-item-hd">
              <span className="mtg-num">{i + 1}.</span>
              <input className="mtg-input" aria-label={`Item ${i + 1} title`} value={item.title} maxLength={500}
                placeholder="What is to be discussed" onChange={(e) => setItem(item.key, { title: e.target.value })} />
              <span className="mtg-icons">
                <button type="button" className="mtg-icon" aria-label={`Move item ${i + 1} up`} disabled={i === 0}
                  onClick={() => set({ items: moveItem(draft.items, item.key, -1) })}>↑</button>
                <button type="button" className="mtg-icon" aria-label={`Move item ${i + 1} down`} disabled={i === draft.items.length - 1}
                  onClick={() => set({ items: moveItem(draft.items, item.key, 1) })}>↓</button>
                <button type="button" className="mtg-icon" aria-label={`Remove item ${i + 1}`}
                  onClick={() => set({ items: draft.items.filter((x) => x.key !== item.key) })}>✕</button>
              </span>
            </div>
            <div className="mtg-item-meta">
              <label className="mtg-field">
                <span>Section</span>
                <input value={item.section} maxLength={100} placeholder="Safety, Schedule…" onChange={(e) => setItem(item.key, { section: e.target.value })} />
              </label>
              <label className="mtg-field">
                <span>Status</span>
                <select value={item.status} onChange={(e) => setItem(item.key, { status: e.target.value })}>
                  {lists.itemStatuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </label>
              <label className="mtg-field">
                <span>Responsible</span>
                <input value={item.responsible} maxLength={200} list={`mtg-responsible-${projectId}`}
                  onChange={(e) => setItem(item.key, { responsible: e.target.value })} />
              </label>
              <label className="mtg-field">
                <span>Due</span>
                <input type="date" value={item.dueDate} onChange={(e) => setItem(item.key, { dueDate: e.target.value })} />
              </label>
            </div>
            <label className="mtg-field mtg-item-minutes">
              <span>Minutes</span>
              <textarea value={item.discussion} maxLength={4000} placeholder="What was said and decided"
                onChange={(e) => setItem(item.key, { discussion: e.target.value })} />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Who is invited, and who came. Picked from the job's team and Contacts, or typed. */
function Attendees({ attendees, directory, onChange, onEdit }: {
  attendees: AttendeeDraft[];
  directory: Awaited<ReturnType<typeof directoryApi.forProject>> | undefined;
  onChange: (a: AttendeeDraft[]) => void;
  onEdit: (key: string, patch: Partial<AttendeeDraft>) => void;
}) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');

  const options = useMemo(() => {
    if (!directory) return { team: [], contacts: [] };
    const names = new Set(attendees.map((a) => a.name.trim().toLowerCase()));
    const picked = new Set(attendees.map((a) => a.contactId).filter((id) => id != null));
    return {
      team: directory.team.filter((t) => t.name && !names.has(t.name.toLowerCase())),
      // A person on the job in two roles is offered once.
      contacts: [...new Map(directory.contacts.filter((c) => !picked.has(c.person.id)).map((c) => [c.person.id, c])).values()],
    };
  }, [directory, attendees]);

  const pick = (value: string) => {
    if (!directory || !value) return;
    if (value.startsWith('t:')) {
      const t = directory.team.find((x) => `t:${x.userId}:${x.role}` === value);
      if (t?.name) onChange([...attendees, attendee(t.name, OUR_COMPANY)]);
    } else if (value.startsWith('c:')) {
      const c = directory.contacts.find((x) => `c:${x.person.id}` === value);
      if (c) onChange([...attendees, attendee(c.person.name, c.company?.name ?? null, c.person.id)]);
    }
  };

  const addTyped = () => {
    if (!name.trim()) return;
    onChange([...attendees, attendee(name.trim(), company.trim() || null)]);
    setName('');
    setCompany('');
  };

  return (
    <div className="pw-card">
      <h2 className="pw-card-hd">Attendees</h2>
      {attendees.length === 0 && <p className="mtg-pad mtg-muted">Nobody yet. Add people from the job, or type a name.</p>}
      {attendees.length > 0 && (
        <div role="list" aria-label="Attendees">
          {attendees.map((a) => (
            <div key={a.key} role="listitem" className="mtg-att">
              {a.contactId != null
                ? <span className="mtg-att-name">{a.name}</span>
                : <input className="mtg-input" aria-label="Attendee name" value={a.name} maxLength={200} onChange={(e) => onEdit(a.key, { name: e.target.value })} />}
              {a.contactId != null
                ? <span className="mtg-muted">{a.company || '—'}</span>
                : <input className="mtg-input" aria-label={`${a.name || 'Attendee'}’s company`} value={a.company} maxLength={200} placeholder="Company" onChange={(e) => onEdit(a.key, { company: e.target.value })} />}
              <select className="mtg-input" aria-label={`Did ${a.name || 'they'} attend?`} value={a.attendance}
                onChange={(e) => onEdit(a.key, { attendance: e.target.value as AttendeeDraft['attendance'] })}>
                <option value="">Not recorded</option>
                <option value="present">Present</option>
                <option value="absent">Absent</option>
              </select>
              <button type="button" className="mtg-icon" aria-label={`Take ${a.name || 'this attendee'} off the list`}
                onClick={() => onChange(attendees.filter((x) => x.key !== a.key))}>✕</button>
            </div>
          ))}
        </div>
      )}
      <div className="mtg-add">
        <select aria-label="Add someone from the job" value="" onChange={(e) => pick(e.target.value)} disabled={!directory}>
          <option value="">{directory ? 'Add someone from the job…' : 'Loading the job’s contacts…'}</option>
          {options.team.length > 0 && (
            <optgroup label="Our team">
              {options.team.map((t) => <option key={`${t.userId}:${t.role}`} value={`t:${t.userId}:${t.role}`}>{t.name} · {t.role}</option>)}
            </optgroup>
          )}
          {options.contacts.length > 0 && (
            <optgroup label="Job contacts">
              {options.contacts.map((c) => (
                <option key={c.person.id} value={`c:${c.person.id}`}>{c.person.name}{c.company ? ` · ${c.company.name}` : ''}</option>
              ))}
            </optgroup>
          )}
        </select>
        <span className="mtg-muted mtg-small">or</span>
        <input aria-label="Name" placeholder="Name" value={name} maxLength={200} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTyped(); } }} />
        <input aria-label="Company" placeholder="Company" value={company} maxLength={200} onChange={(e) => setCompany(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTyped(); } }} />
        <Button size="sm" variant="secondary" onClick={addTyped} disabled={!name.trim()}>Add</Button>
      </div>
    </div>
  );
}

/** A meeting for someone who can see the job but not edit it. */
function MeetingReadView({ meeting: m, lists, onClose }: { meeting: MeetingDetail; lists: ProjectMeetings; onClose: () => void }) {
  const statusLabel = (k: string, list: { key: string; label: string }[]) => list.find((s) => s.key === k)?.label ?? k;
  const present = m.attendees.filter((a) => a.attendance === 'present');
  const absent = m.attendees.filter((a) => a.attendance === 'absent');
  const other = m.attendees.filter((a) => !a.attendance);
  const who = (list: typeof m.attendees) => list.map((a) => (a.company ? `${a.name} (${a.company})` : a.name)).join(', ');
  return (
    <div className="mtg mtg-form">
      <MeetingStyles />
      <div className="mtg-bar">
        <button type="button" className="mtg-back" onClick={onClose}>← All meetings</button>
      </div>
      <div className="pw-card">
        <h2 className="pw-card-hd">{m.name}</h2>
        <p className="mtg-pad">
          {fmtSentAt(m.startsAt)}{m.location ? ` · ${m.location}` : ''}{' '}
          <Badge label={statusLabel(m.status, lists.statuses)} tone={STATUS_TONE[m.status] ?? 'neutral'} />
        </p>
        {present.length > 0 && <p className="mtg-pad mtg-small"><strong>Present:</strong> {who(present)}</p>}
        {absent.length > 0 && <p className="mtg-pad mtg-small"><strong>Absent:</strong> {who(absent)}</p>}
        {other.length > 0 && <p className="mtg-pad mtg-small"><strong>{present.length || absent.length ? 'Also invited' : 'Invited'}:</strong> {who(other)}</p>}
      </div>
      <div className="pw-card">
        <h2 className="pw-card-hd">Agenda and minutes</h2>
        {m.items.length === 0 && <p className="mtg-pad mtg-muted">No items.</p>}
        {m.items.map((i, n) => (
          <div key={i.entryId}>
            {i.section && i.section !== m.items[n - 1]?.section && <div className="mtg-section">{i.section}</div>}
            <div className="mtg-read-item">
              <span className="mtg-num">{n + 1}.</span>
              <div>
                <div className="mtg-read-title">{i.title}</div>
                <div className="mtg-read-meta">
                  <span>{statusLabel(i.status, lists.itemStatuses)}</span>
                  {i.responsible && <span>Responsible: {i.responsible}</span>}
                  {i.dueDate && <span>Due {fmtDate(i.dueDate)}</span>}
                </div>
                {i.discussion && <p className="mtg-read-minutes">{i.discussion}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
